/**
 * THE id generator. One implementation, two callers.
 *
 * WHY IT LIVES HERE AND NOT IN src/. 04-schema.md: `block_id` is "a lowercase UUIDv7
 * minted in the browser", and "the editor extension's documented default generator produces a v4
 * UUID and would fail every insert on day one, so the v7 generator must be exported from
 * `shared/content-model` and configured explicitly". 03-worksheet.md (signed off) says
 * the same and sizes it: "a ~20-line generator exported from `shared/content-model` is needed from
 * day one". `src/common/ids` RE-EXPORTS this file with a plain relative import; it never
 * re-implements it. adr/0008 is the discipline: "One anchor implementation, two callers."
 *
 * NO npm PACKAGE. `crypto.randomUUID()` produces v4 only and there is no built-in v7
 * (research/19-block-content-anchors-research.md). A sweep of the corpus finds no
 * package-shaped uuidv7 reference anywhere; the signed-off worksheet takes the hand-rolled side.
 *
 * THIS FILE MUST NOT NORMALISE. shared/content-model also owns NFC-on-write for text columns
 * (03-worksheet.md, WD-134). Ids never go through that: 04-schema.md keeps a legacy
 * Firestore id "verbatim", and 03-domain-model.md says name-shaped ids are in every article
 * URL "which must keep working". A silently re-normalised author id is a dead URL.
 */

/**
 * The block-id format CHECK, byte-for-byte.
 *
 * Declared twice as live DDL: `ck_awb_id_format` on `article_working_block`
 * (04-schema.md) and `ck_avb_id_format` on `article_version_block`
 * (04-schema.md). If this string and that constraint ever differ, the browser mints ids
 * the database refuses. (Research note 19 calls the constraint `ck_ab_id_format` - that name does
 * not exist at HEAD; do not grep for it.)
 *
 * Note the VARIANT nibble `[89ab]` in position 17, not just the version `7` in position 13. A
 * generator that stamps the version and forgets RFC 9562's variant bits passes every naive
 * `charAt(14) === '7'` check and then fails every block insert.
 */
export const UUIDV7_CHECK_CONSTRAINT_SOURCE =
  '^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$';

/**
 * The same pattern as a regex. A later consumer imports THIS constant rather than typing a second
 * copy: 04-schema.md lets the file reconciler auto-delete a stray object only if "it
 * matches our lowercase-UUIDv7 naming pattern", and 04-schema.md states the cost of a looser
 * copy - "that mistake deletes a live article's image".
 */
export const UUIDV7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * Is this string a lowercase RFC 9562 version-7 UUID?
 *
 * This is the STRICT validator: for ids WE mint and for `block_id`. It is the one that rejects a
 * v4 (12-build-plan.md). It is NOT the validator for a public `<entity>_id` coming off the
 * wire - see `isPublicId` in src/common/ids, and read the reason there before reaching for this
 * one on a request path.
 */
export function isUuidV7(value: string): boolean {
  return UUIDV7_RE.test(value);
}

const MAX_COUNTER = 0xfff; // rand_a is 12 bits
const COUNTER_SEED_MASK = 0x7ff; // seed in the lower half, so a millisecond has >= 2048 of headroom

let lastTimestampMs = -1;
let lastCounter = 0;

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

function seedCounter(): number {
  const seed = randomBytes(2);
  return (((seed[0] ?? 0) << 8) | (seed[1] ?? 0)) & COUNTER_SEED_MASK;
}

/**
 * Mint a lowercase UUIDv7.
 *
 * THE INSTANT IS A PARAMETER, AND THAT IS DELIBERATE. C11 (adr/0013) bans `Date.now()` and
 * `new Date()` outside the one clock implementation, and this module is imported by the browser
 * editor AND by the server. A zero-argument `uuidv7()` would have to read a global here, which
 * would mean a SECOND exempt file in the one module both callers share. Taking the instant instead
 * keeps the exemption at exactly one file, and it makes "minted ids sort by time"
 * (12-build-plan.md) a deterministic assertion under a test clock rather than a race that
 * passes on a slow machine. The browser side already has to write
 * `UniqueID.configure({ generateID: ... })` by hand (04-schema.md), so the one call site the
 * corpus requires to be explicit is the one that passes its clock.
 *
 * MONOTONIC WITHIN A MILLISECOND. RFC 9562's own method: the 12-bit `rand_a` field is used as a
 * counter, seeded randomly when the millisecond advances and incremented while it does not. On
 * overflow the timestamp advances by one millisecond and the counter is re-seeded. This keeps the
 * value inside the CHECK above and makes 12-build-plan.md's "sorts by time" true even for two
 * ids minted in the same millisecond, which a textbook v7 does not give.
 *
 * @param now the current instant, from the injected clock (`Clock.now()`).
 */
export function uuidv7(now: Date): string {
  const requestedMs = now.getTime();
  if (!Number.isFinite(requestedMs)) {
    throw new RangeError('uuidv7: the instant passed in is not a valid Date');
  }
  if (requestedMs < 0) {
    throw new RangeError('uuidv7: instants before 1970-01-01T00:00:00Z cannot be encoded');
  }

  let timestampMs: number;
  let counter: number;

  if (requestedMs > lastTimestampMs) {
    timestampMs = requestedMs;
    counter = seedCounter();
  } else {
    // Same millisecond, or a clock that moved backwards. Either way the value must not go back.
    timestampMs = lastTimestampMs;
    counter = lastCounter + 1;
    if (counter > MAX_COUNTER) {
      timestampMs = lastTimestampMs + 1;
      counter = seedCounter();
    }
  }

  lastTimestampMs = timestampMs;
  lastCounter = counter;

  const bytes = new Uint8Array(16);
  const high16 = Math.floor(timestampMs / 0x100000000); // bits 47..32
  const low32 = timestampMs >>> 0; // bits 31..0

  bytes[0] = (high16 >>> 8) & 0xff;
  bytes[1] = high16 & 0xff;
  bytes[2] = (low32 >>> 24) & 0xff;
  bytes[3] = (low32 >>> 16) & 0xff;
  bytes[4] = (low32 >>> 8) & 0xff;
  bytes[5] = low32 & 0xff;

  // version 7 in the high nibble of byte 6, then the top 4 bits of the 12-bit counter
  bytes[6] = 0x70 | ((counter >>> 8) & 0x0f);
  bytes[7] = counter & 0xff;

  const tail = randomBytes(8);
  // variant 0b10 in the top two bits of byte 8 - this is the `[89ab]` the CHECK demands
  bytes[8] = 0x80 | ((tail[0] ?? 0) & 0x3f);
  for (let i = 1; i < 8; i += 1) {
    bytes[8 + i] = tail[i] ?? 0;
  }

  return formatUuid(bytes);
}

function formatUuid(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    out += (bytes[i] ?? 0).toString(16).padStart(2, '0');
    if (i === 3 || i === 5 || i === 7 || i === 9) {
      out += '-';
    }
  }
  return out;
}

/**
 * Reset the monotonic state. FOR TESTS ONLY - a test that mints under a frozen clock and then
 * rewinds that clock needs the counter to start again, and nothing in production ever should.
 */
export function resetUuidV7MonotonicStateForTests(): void {
  lastTimestampMs = -1;
  lastCounter = 0;
}
